import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { ModernSliderWrapper } from '@components/dashboard/SharedStyles';
import Colors from '@components/shared/Colors';
import { PageSlice, PathogenCodes, SliceScreeningParametersInfoSliderDetails } from '@contracts';
import { getPathogensData } from '@data/pathogensData';
import CarouselChevron from '@images/CarouselChevron';
import useCarousel from '@utils/useCarousel';
import Carousel from './Carousel';
import ScreeningParametersInfoMobileItem from './ScreeningParametersInfoMobileItem';
import ScreeningParametersInfoSliderItem from './ScreeningParametersInfoSliderItem';
import ScreeningParametersInfoSliderPageHeading from './ScreeningParametersInfoSliderStiPageHeading';
import ScreeningParametersInfoSliderProductHeading from './ScreeningParametersInfoSliderStiProductHeading';
import useDesktop from './useDesktop';

interface Props {
  slice: PageSlice;
}

const flickityOptions = {
  initialIndex: 0,
  groupCells: 2,
  pageDots: true,
  prevNextButtons: false,
  contain: true,
  percentPosition: false,
  cellAlign: 'left',
  autoPlay: false,
  draggable: true
};

const ScreeningParametersInfoSlider = ({ slice }: Props) => {
  const { t } = useTranslation();
  const isDesktop = useDesktop(768);
  const { carouselRef, carouselNext, nextDisabled, carouselPrevious, previousDisabled } = useCarousel({ initialIndex: flickityOptions.initialIndex });
  const sliceDetails = slice?.primary as SliceScreeningParametersInfoSliderDetails;
  const pathogensData = getPathogensData({
    codes: [
      PathogenCodes.chlamydia,
      PathogenCodes.gonorrhoea,
      PathogenCodes.trichomoniasis,
      PathogenCodes.herpes_with_types,
      PathogenCodes.hpv_screening
    ]
  });

  if (!isDesktop) {
    return (
      <Container>
        <HeadWrapper>
          {sliceDetails.type === 'sti' ? <ScreeningParametersInfoSliderProductHeading /> : null}
          {sliceDetails.type === 'sti-landing' ? <ScreeningParametersInfoSliderPageHeading /> : null}
        </HeadWrapper>
        <ItemsWrapper>
          {pathogensData.map((item, index) => (
            <ScreeningParametersInfoMobileItem
              key={index}
              background={item.background}
              title={t(item.title)}
              image={item.image}
              info={t(item.info || '')}
              symptoms={(item.symptoms || []).map((symptom) => t(symptom))}
              comingSoon={item.comingSoon}
            />
          ))}
        </ItemsWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <HeadWrapper>
        {sliceDetails.type === 'sti' ? <ScreeningParametersInfoSliderProductHeading /> : null}
        {sliceDetails.type === 'sti-landing' ? <ScreeningParametersInfoSliderPageHeading /> : null}
        <ControlsWrap>
          <CarouselPrevious onClick={carouselPrevious} disabled={previousDisabled} aria-label="Carousel previous page">
            <CarouselChevron />
          </CarouselPrevious>
          <CarouselNext onClick={carouselNext} disabled={nextDisabled} aria-label="Carousel next page">
            <CarouselChevron />
          </CarouselNext>
        </ControlsWrap>
      </HeadWrapper>
      <ItemsWrapper>
        <ModernSliderWrapper>
          <Carousel ref={carouselRef} options={flickityOptions}>
            {pathogensData.map((item, index) => (
              <ScreeningParametersInfoSliderItem
                key={index}
                background={item.background}
                title={t(item.title)}
                image={item.image}
                info={t(item.info || '')}
                symptoms={(item.symptoms || []).map((symptom) => t(symptom))}
                comingSoon={item.comingSoon}
              />
            ))}
          </Carousel>
        </ModernSliderWrapper>
      </ItemsWrapper>
    </Container>
  );
};

export default ScreeningParametersInfoSlider;

const Container = styled.div`
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  padding: 100px 120px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 50px 20px;
    max-width: 400px;
    width: 100%;
    margin: 0 auto;
  }
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
    justify-content: center;
  }
`;

const ControlsWrap = styled.div`
  display: flex;
  gap: 30px;
  align-self: start;
`;

const CarouselNext = styled.button`
  background: none;
  border: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  svg {
    width: 12px;
  }

  path {
    fill: ${Colors.navyBlue};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CarouselPrevious = styled(CarouselNext)`
  transform: rotate(180deg);

  @media (max-width: 768px) {
    display: none;
  }
`;

const ItemsWrapper = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .carousel {
    max-height: 460px;
  }

  .carousel:after {
    content: 'flickity';
    display: none; /* hide :after */
  }

  .flickity-viewport {
    overflow: visible !important;
  }

  .flickity-page-dots {
    bottom: -40px;

    @media (max-width: 991px) {
      bottom: -20px;
    }
  }

  .card-main-row {
    padding: 20px 30px 0px 30px;

    @media (max-width: 768px) {
      padding: 20px 25px 20px 25px;
    }
  }

  .card-content {
    padding: 15px 30px 20px 30px;
    height: 350px;

    @media (max-width: 768px) {
      padding: 0 25px 20px 25px;
      height: auto;
    }
  }
`;
